import React, { useEffect, useState } from 'react';
import { Col, ControlLabel, FormGroup, Row } from 'react-bootstrap';
import t from '../../../../../../lib/I18n';
import PropTypes from 'prop-types';
import ValidatedFormField from '../../../../../form/ValidatedField';
import ADRPaymentMethodsForm from '../../../../../../records/customer/ADRPaymentMethodsForm';
import { oneClickDelayRepayReimbursementMethods } from '../../../../../../constants/AppConstants';
import RadioWrapper from '../../../../../form/Radio';
import Assets from '../../../../../../helpers/Assets';
import FormattedHTMLMessage from '../../../../../general/FormattedHTMLMessage';
import { OcdrCharities } from '../../../../../../constants/OcdrCharities';
import config from '../../../../../../config';
import DelayRepayService from '../../../../../../services/DelayRepayService';
import Icon from '../../../../../general/Icon';

export const PaymentForms = ({ form, updateFormField, isValidBACS }) => {
    const [activeCharities, setActiveCharities] = useState([]);
    const [charitiesLoading, setCharitiesLoading] = useState(false);
    const { method, sortCode, accountNumber, charity } = form;

    useEffect(() => {
        if (method === oneClickDelayRepayReimbursementMethods.BACS && form.isComplete()) {
            isValidBACS(sortCode, accountNumber);
        }
    }, [sortCode, accountNumber, isValidBACS, form, method]);

    useEffect(() => {
        const fetchCharities = async () => {
            setCharitiesLoading(true);
            const charities = await DelayRepayService.getCharities();
            setActiveCharities(charities);
            setCharitiesLoading(false);
        };

        fetchCharities();
    }, []);

    return (
        <Row>
            <form
                className={
                    method === oneClickDelayRepayReimbursementMethods.PAYPAL
                        ? 'auto-dr__payment-form margin-right'
                        : ''
                }
                onSubmit={e => e.preventDefault()}
            >
                <Row className="margin-left--xs margin-right--xs">
                    {method === oneClickDelayRepayReimbursementMethods.PAYPAL && (
                        <FormGroup>
                            <ControlLabel>
                                {t('auto-dr.account.repayment.method.paypal-email-address')}
                            </ControlLabel>
                            <ValidatedFormField
                                type="email"
                                field="paypalEmail"
                                form={form}
                                id="paypal-email"
                                className="input-field margin-bottom--sm"
                                onChange={updateFormField}
                                required
                                label="PayPal email address"
                            />
                        </FormGroup>
                    )}
                    {method === oneClickDelayRepayReimbursementMethods.BACS && (
                        <div className='auto-dr__charity--bacs'>
                            <FormGroup>
                                <ControlLabel>
                                    {t('auto-dr.account.repayment.method.sort-code')}
                                </ControlLabel>
                                <ValidatedFormField
                                    type="text"
                                    form={form}
                                    id="sort-code"
                                    field="sortCode"
                                    className="input-field margin-bottom--sm"
                                    placeholder={t('auto-dr.claim-form.sortCodePlaceholder')}
                                    onChange={updateFormField}
                                    required
                                    label={t('auto-dr.claim-form.sortCodeLabel')}
                                    aria-label={t('auto-dr.claim-form.sortCodeAriaLabel', { sortCode })}
                                    speakDigits
                                />
                                <ControlLabel>
                                    {t('auto-dr.account.repayment.method.account-number')}
                                </ControlLabel>
                                <ValidatedFormField
                                    type="text"
                                    form={form}
                                    field="accountNumber"
                                    id="account-number"
                                    className="input-field margin-bottom--sm"
                                    placeholder={t('auto-dr.claim-form.accountNumberPlaceholder')}
                                    onChange={updateFormField}
                                    required
                                    label={t('auto-dr.claim-form.accountNumber')}
                                    aria-label={t('auto-dr.claim-form.accountNumberAriaLabel', { accountNumber })}
                                    speakDigits
                                />
                            </FormGroup>
                        </div>
                    )}
                    {method === oneClickDelayRepayReimbursementMethods.CHARITY && (
                        <Col>
                            {charitiesLoading ? (
                                <Icon icon="spinner" className="otrl otrl-spinner otrl-spin" />
                            ) : activeCharities && activeCharities.length === 0 ? (
                                <p className="text-center">{t('charity.loading.error')}</p>
                            ) : (
                                <FormGroup role="radiogroup">
                                    {OcdrCharities.map(
                                        ({
                                            value,
                                            imageUrl,
                                            nameKey,
                                            descriptionKey,
                                            website,
                                            linkText,
                                            featureFlagName,
                                        }) => {
                                            if (!config.get(featureFlagName)) {
                                                return null;
                                            }

                                            if (!activeCharities || !activeCharities.find(activeCharity => activeCharity.name === value)) {
                                                return null;
                                            }

                                            const selected = charity === value;

                                            return (
                                                <RadioWrapper
                                                    className={selected ? "auto-dr__charity--radio-selected" : "auto-dr__charity--radio"}
                                                    name="type-horizontal"
                                                    label={
                                                        <div className="auto-dr__charity--label">
                                                            <div className="auto-dr__charity--label-heading">
                                                                <span className="delivery-message">
                                                                    {t(nameKey)}
                                                                </span>
                                                                <img
                                                                    src={Assets.assetUrl(imageUrl)}
                                                                    alt={t(nameKey)}
                                                                    className={"auto-dr__charity--logo"}
                                                                />
                                                            </div>
                                                            {selected && (
                                                                <ControlLabel className="margin-top--xs">
                                                                    <FormattedHTMLMessage
                                                                        message={descriptionKey}
                                                                        charityUrl={website}
                                                                        urlText={linkText}
                                                                    />
                                                                </ControlLabel>
                                                            )}
                                                        </div>
                                                    }
                                                    aria-hidden="false"
                                                    tabIndex={0}
                                                    aria-checked={selected}
                                                    aria-label={`${t(nameKey)} ${t(descriptionKey, { charityUrl: website, urlText: linkText })}`}
                                                    role="radio"
                                                    value={value}
                                                    checked={selected}
                                                    onChange={() => updateFormField('charity', value)}
                                                    hasFocus
                                                />
                                            );
                                        },
                                    )}
                                </FormGroup>
                            )}
                        </Col>
                    )}
                </Row>
            </form>
        </Row>
    );
};

PaymentForms.propTypes = {
    method: PropTypes.string.isRequired,
    onSubmit: PropTypes.func.isRequired,
    updateFormField: PropTypes.func.isRequired,
    form: PropTypes.instanceOf(ADRPaymentMethodsForm).isRequired,
    isValidBACS: PropTypes.func,
};



// WEBPACK FOOTER //
// ./src/components/account/subscribeDelayRepay/components/SelectPaymentMethod/components/PaymentForms.js